// ============================================================
// BandePieces — une vignette par PIÈCE, et LA sélection de l'écran.
// ------------------------------------------------------------
// Ce composant fait deux choses, et la seconde est la plus importante.
//
// 1. Il rend un devis de 22 pièces navigable. La grille précédente
//    listait des PAGES, sans état ni prix : à 22 pièces on ne trouvait
//    plus rien.
//
// 2. **Il porte la sélection unique de l'écran.** C'est ce qui a
//    supprimé le défaut le plus dangereux du projet : le visualiseur
//    affichait la page 2 pendant que le bloc Temps montrait les chiffres
//    de la page 1, sans aucun signe. Deux états indépendants — la page
//    affichée et la dernière lecture faite — pouvaient diverger, et
//    l'écran affirmait quelque chose de faux sans rien signaler.
//
// Désormais une seule sélection gouverne le plan, la lecture, les temps
// et le prix. Changer de pièce change TOUT, ou ne montre rien.
//
// ── La bande reste visible ──
//
// `position: sticky`. Sur 22 pièces, une bande qui défile hors de vue
// oblige à remonter à chaque changement — et c'est exactement le geste
// qu'on veut rendre gratuit.

import { useEffect, useRef } from 'react';
import { libelleFeuilles } from './feuillesLibelles.js';
import Badge from '../../ui/Badge.jsx';
import { formatEuroOuTiret } from '../../lib/format.js';
import { chargeAtelier, minutesDuLot, HEURES_PAR_JOUR } from '@shared/gamme.js';
import { prixUnitaireToutCompris } from '@shared/pricing.js';
import { chargeDeLaPiece, libelleCharge } from './chargePiece.js';

/** Les états d'une pièce, avec leur ton. Aucun nom de code à l'écran. */
const ETATS = {
  chiffree: { tone: 'ok', label: 'Chiffrée' },
  estimee: { tone: 'neutral', label: 'Chiffrée — temps estimés' },
  a_verifier: { tone: 'warn', label: 'À vérifier' },
  a_estimer: { tone: 'neutral', label: 'À chiffrer' },
  incomplete: { tone: 'warn', label: 'À compléter' },
  lecture: { tone: 'neutral', label: 'Lecture en cours' },
  estimation: { tone: 'neutral', label: 'Estimation en cours' },
  en_attente: { tone: 'neutral', label: 'En attente' },
  erreur: { tone: 'danger', label: 'Erreur' },
};

/**
 * L'état d'une pièce, déduit de ce qu'elle porte. Jamais auto-déclaré.
 *
 * ── « Prix bloqué » n'existe plus ──
 *
 * Il se déclenchait sur `warnings.length` — donc sur un `info` comme sur
 * une `error`. Une pièce parfaitement chiffrée dont la seule remarque
 * était « prix au kilo estimé par famille » s'affichait « Prix bloqué ».
 *
 * Et le mot lui-même était faux depuis le lot D : **rien ne bloque un
 * prix.** Ce qui existe, c'est une pièce dont personne n'a encore
 * estimé les temps (« À chiffrer », et le geste est à un clic) et une
 * pièce à laquelle il manque une valeur que personne ne peut deviner
 * (« À compléter »).
 */
/**
 * UNE PIÈCE QUI N'A NI GAMME NI TEMPS N'A PAS ÉTÉ CHIFFRÉE.
 *
 * Exporté parce que DEUX écrans en ont besoin — la bande et la carte —
 * et qu'ils s'étaient déjà contredits : la bande disait « à vérifier »,
 * la carte « 1 à compléter », et le bouton entre les deux « Chiffrer la
 * pièce ». Trois mots, un seul geste.
 *
 * `etatDePiece` place « à vérifier » DEVANT — c'est un signal sur la
 * lecture, pas un geste de chiffrage, et il doit se voir. La carte, elle,
 * pose la question autrement : que dois-je faire maintenant ?
 */
export function estAChiffrer(piece) {
  const sansGamme = !(piece?.gamme ?? []).length;
  const sansTemps = piece?.cycle_time_min == null && piece?.cycle_time_min_ia == null;
  return sansGamme && sansTemps;
}

/**
 * ── « À CHIFFRER » N'EST PLUS UN RESTE, C'EST L'ÉTAT NORMAL ──
 *
 * Depuis le 27 août, le dépôt d'un plan ne lance plus que la LECTURE :
 * la consigne du deviseur ne peut pas exister au moment où on estime.
 * Une pièce fraîchement lue arrive donc SANS temps, et c'est voulu.
 *
 * Le mot compte : l'étiquette dit le même geste que le bouton qui la
 * lève — « Chiffrer la pièce ». Deux mots pour un geste feraient croire
 * à deux gestes.
 */
export function etatDePiece(piece, result) {
  if (piece?.a_verifier) return 'a_verifier';
  if (result?.price_unit != null && result.complete !== false) {
    // Un prix bâti sur des temps non validés est un prix, mais il ne se
    // présente pas comme un prix validé.
    const estime = Object.values(result?.detail?.work?.sources ?? {}).includes('estime');
    return estime ? 'estimee' : 'chiffree';
  }
  const erreurs = (result?.warnings ?? []).filter((w) => w.level === 'error');

  // ── « À CHIFFRER » PASSE AVANT « À COMPLÉTER » — 27 août 2026 ──
  //
  // Une pièce qui n'a NI gamme NI temps proposés n'a pas été chiffrée,
  // et c'est le premier geste — quels que soient ses autres manques. La
  // matière, le brut et le reste se règlent après, et la plupart du
  // temps le chiffrage les fait tomber tout seuls.
  //
  // Vérifié à l'écran le jour même : sur une pièce fraîchement lue,
  // l'étiquette disait « à compléter » alors que le bouton juste à côté
  // proposait « Chiffrer la pièce ». Deux mots pour un état, et le
  // premier n'indiquait pas le geste à faire.
  if (estAChiffrer(piece)) return 'a_estimer';

  if (erreurs.some((w) => w.field === 'temps')) return 'a_estimer';
  if (erreurs.length) return 'incomplete';
  return 'en_attente';
}

export function BandePieces({ pieces = [], selection, onSelect, total, aVerifier = 0, pages = [], feuilles = {} }) {
  // Une pièce née d'une DÉCOUPE le dit (14 octobre 2026) : deux pièces
  // « page 1 » sans autre mention se liraient comme un doublon.
  const decoupee = (piece) => pages.find((pg) => pg.file_id === piece.plan_file_id && pg.decoupe_de);
  const refs = useRef([]);

  /** La charge de TOUT le devis : la somme des lots de chaque pièce. */
  const charge = chargeAtelier(
    pieces.reduce((somme, p) => somme + minutesDuLot(p?.result?.detail?.work ?? {}), 0),
  );

  // La touche du clavier suit la sélection : sans ça, une flèche déplace
  // la sélection mais laisse le focus derrière, et le lecteur d'écran
  // annonce l'ancienne pièce.
  useEffect(() => {
    refs.current[selection]?.focus?.({ preventScroll: false });
  }, [selection]);

  function onKeyDown(e) {
    let suivant = null;
    if (e.key === 'ArrowRight') suivant = Math.min(pieces.length - 1, selection + 1);
    else if (e.key === 'ArrowLeft') suivant = Math.max(0, selection - 1);
    else if (e.key === 'Home') suivant = 0;
    else if (e.key === 'End') suivant = pieces.length - 1;
    if (suivant === null) return;
    e.preventDefault();
    onSelect(suivant);
  }

  if (pieces.length === 0) return null;

  return (
    <div className="bande">
      <div
        className="bande__liste"
        role="tablist"
        aria-label={`${pieces.length} pièce(s) du devis`}
      >
        {pieces.map(({ piece, result }, i) => {
          const etat = ETATS[etatDePiece(piece, result)] ?? ETATS.en_attente;
          return (
            <button
              key={piece.id ?? `n${i}`}
              type="button"
              role="tab"
              aria-selected={i === selection}
              tabIndex={i === selection ? 0 : -1}
              ref={(el) => {
                refs.current[i] = el;
              }}
              onKeyDown={onKeyDown}
              className={`bande__vignette${i === selection ? ' bande__vignette--active' : ''}`}
              onClick={() => onSelect(i)}
            >
              <span className="bande__num">{i + 1}</span>
              <span className="bande__nom">{piece.name || piece.ref || 'Sans désignation'}</span>
              {/* La RÉFÉRENCE, toujours. C'est elle qui rend une
                  incohérence visible d'un coup d'œil. */}
              <span className="bande__ref">{piece.ref || '—'}</span>
              {decoupee(piece) ? (
                <span className="field__hint">{`découpée de la page ${decoupee(piece).page_number ?? '?'}`}</span>
              ) : null}
              {/* Une pièce sur plusieurs feuilles le DIT (4 novembre 2026). */}
              {libelleFeuilles(feuilles[piece.id]) ? <span className="field__hint">{libelleFeuilles(feuilles[piece.id])}</span> : null}
              <Badge tone={etat.tone}>{etat.label}</Badge>
              <span className="bande__prix num">{formatEuroOuTiret(result?.price_unit)}</span>
              {/* Tout compris (27 octobre 2026) : seulement quand la part
                  indicative existe — sinon le même nombre deux fois. */}
              {Number(result?.detail?.total_hors_engagement) > 0 && prixUnitaireToutCompris(result) != null ? (
                <span className="bande__tout-compris num">
                  {`${formatEuroOuTiret(prixUnitaireToutCompris(result))} tout compris`}
                </span>
              ) : null}
              {/* Le temps de la PIÈCE, comme celui du devis (Philippe, 6 octobre
                  2026) : « — » tant qu'elle n'est pas chiffrée. */}
              <span className="bande__charge-piece">
                {chargeDeLaPiece(result)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bande__total">
        <p className="caps">Total du devis</p>
        <p className="bande__montant num">{formatEuroOuTiret(total)}</p>
        {/* Une seule pièce : son prix tout compris, sous le total. Sur
            plusieurs références, il est dans chaque vignette — un total
            divisé par une quantité ne désignerait aucune pièce. */}
        {pieces.length === 1 && prixUnitaireToutCompris(pieces[0].result) != null ? (
          <p className="bande__tout-compris num">
            {`soit ${formatEuroOuTiret(prixUnitaireToutCompris(pieces[0].result))} par pièce, matière et traitement compris`}
          </p>
        ) : null}
        {/* ── LA CHARGE, EN HEURES ET EN JOURS — 1er septembre 2026 ──
            Raph : « je veux pouvoir dire *on en a pour une semaine* sans
            recalculer. » Les minutes et les euros ne répondent pas à une
            question de PLANNING.

            La base de 8 h est écrite en clair : c'est une convention
            d'affichage pour situer un ordre de grandeur, pas un délai
            promis — et un nombre de jours sans sa base se lirait comme
            une date. */}
        {charge.minutes > 0 ? (
          <p className="bande__charge">
            <strong>{libelleCharge(charge)}</strong>
            <span className="field__hint"> (base {HEURES_PAR_JOUR} h)</span>
          </p>
        ) : null}
        {/* ── « À VÉRIFIER » EST UN SIGNAL, PAS UNE SOUSTRACTION ──
            Depuis le 25 août 2026, une pièce à vérifier COMPTE dans le
            total (§6, règle 2). La phrase disait encore « exclue(s) du
            total » sous un montant qui l'incluait — vu sur le devis
            2026-09-040 le 15 septembre : 471,58 €, dont les 452 € de la
            seule pièce, annoncée exclue. */}
        {aVerifier > 0 ? (
          <p className="field__hint">
            <strong>{aVerifier} pièce{aVerifier > 1 ? 's' : ''} à vérifier</strong>
            {' '}— comptée{aVerifier > 1 ? 's' : ''} dans le total, à relire avant l’envoi.
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default BandePieces;
